import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import LoadingScreen from "../Utils/LoadingScreen";
import { Plus, Trash } from "lucide-react";
import imageCompression from "browser-image-compression";

const ViewBanner = () => {
  const { baseurl, token, setIsUnauthorised ,AdminId } = useGlobalContext();
  const [Loading, setLoading] = useState(false);
  const [bannersData, setBannersData] = useState([]);
  const [galleryData, setGalleryData] = useState([]);
  const [isCreatingBanner, setIsCreatingBanner] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    imageURL: "",
    imageTitle: "",
    imageDesctription: "",
    backgroundColor: "#ffffff",
    titleColor: "#000000",
    descriptionColor: "#666666",
    adminId:AdminId
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/getAllBanners`,
        { userid: "",adminId:AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );
      if (res.data.responseCode === "00")
        setBannersData(res.data.response || []);
      else if (res.data.responseCode === "401") setIsUnauthorised(true);
      else toast.error(res.data.responseMessage);
    } catch {
      toast.error("Error fetching banners");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      imageURL: "",
      imageTitle: "",
      imageDesctription: "",
      backgroundColor: "#ffffff",
      titleColor: "#000000",
      descriptionColor: "#666666",
    });
    setSelectedImage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const selectFromGallery = (url) => {
    setForm({ ...form, imageURL: url });
    setSelectedImage(url);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 600,
      useWebWorker: true,
    };
    const compressed = await imageCompression(file, options);
    return compressed;
  };

  const uploadImage = async (file) => {
    setUploadingImage(true);
    try {
      const compressedFile = await compressImage(file);

      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          let base64 = reader.result;
          base64 = base64.replace(
            /^data:image\/(jpeg|png|jpg|webp);base64,/,
            ""
          );
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(compressedFile);
      const base64Image = await base64Promise;

      const payload = {
        userid: "banner_user",
        item_name: `banner_${Date.now()}`,
        image_data: base64Image,
        type: "banner",adminId:AdminId
      };

      const res = await axios.post(
        "https://bumppy.in/packky/api/v1/uploadKycImage.php",
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.status === "0" || res.data.status === 0) {
        return res.data.image_url || "";
      } else {
        throw new Error(res.data.msg || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Image upload failed");
      return "";
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadedUrl = await uploadImage(file);
    if (uploadedUrl) {
      setForm({ ...form, imageURL: uploadedUrl });
      setSelectedImage(uploadedUrl);
    }
  };

  const deleteBanner = async (bannerid) => {
    if (!bannerid) return;
    const ok = window.confirm("Are you sure you want to delete this banner?");
    if (!ok) return;

    setDeletingId(bannerid);
    try {
      const payload = { bannerid,adminId:AdminId };
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/deleteBanners`,
        payload,
        {
          headers: { Authorization: `Basic ${token}` },
        }
      );

      if (res.data.responseCode === "00") {
        toast.success("Banner deleted");
        await fetchBanners();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        toast.error("Unauthorized");
      } else {
        toast.error(res.data.responseMessage || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Error deleting banner");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.imageURL) return toast.error("Please select an image");
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/insertBanners`,
        form,
        {
          headers: { Authorization: `Basic ${token}` },
        }
      );
      if (res.data.responseCode === "00") {
        toast.success("Banner created successfully!");
        resetForm();
        setIsCreatingBanner(false);
        fetchBanners();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
        toast.error("Unauthorized access!");
      } else {
        toast.error(res.data.responseMessage || "Error creating banner!");
      }
    } catch {
      toast.error("Error creating banner!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] p-6">
      {!isCreatingBanner && (
        <>
          <div className="flex items-end justify-end gap-4 mb-4">
            <button
              className="flex items-center space-x-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() => setIsCreatingBanner(true)}
            >
              <Plus size={14} />
              <span>Create Banner</span>
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 w-full max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-center mb-6">
              All Banners
            </h2>
            {Loading ? (
              <LoadingScreen />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {bannersData.length > 0 ? (
                  bannersData.map((banner) => (
                    <div
                      key={banner.id}
                      className="rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all relative bg-white"
                      style={{
                        backgroundColor: banner.background_color || "#ffffff",
                      }}
                    >
                      {banner.image_url && (
                        <div className="relative w-full h-64 md:h-72 lg:h-80 overflow-hidden">
                          <img
                            src={banner.image_url}
                            alt={banner.img_title}
                            className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}

                      <div className="p-4">
                        <h3
                          className="text-lg font-bold mb-2"
                          style={{ color: banner.title_color || "#000000" }}
                        >
                          {banner.img_title}
                        </h3>
                        <p
                          className="text-sm leading-relaxed"
                          style={{
                            color: banner.description_color || "#666666",
                          }}
                        >
                          {banner.image_description}
                        </p>
                      </div>

                      <button
                        onClick={() => deleteBanner(banner.id)}
                        disabled={deletingId === banner.id}
                        className="absolute bottom-3 right-3 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition"
                        title="Delete banner"
                        style={{ backdropFilter: "blur(6px)" }}
                      >
                        <Trash size={18} className="text-red-600" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    No banners available
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {isCreatingBanner && (
        <div className="max-w-6xl mx-auto bg-white p-6 rounded-xl shadow flex flex-col md:flex-row gap-6">
          <form onSubmit={handleSubmit} className="flex-1 space-y-4">
            <h2 className="text-2xl font-bold mb-4 text-center md:text-left">
              Create Banner
            </h2>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full p-2 border rounded"
            />
            {uploadingImage && (
              <p className="text-sm text-gray-500">Uploading image...</p>
            )}

            <input
              type="text"
              name="imageTitle"
              placeholder="Title"
              value={form.imageTitle}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />

            <textarea
              name="imageDesctription"
              placeholder="Description"
              value={form.imageDesctription}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />

            <div className="flex items-center justify-between space-x-4 mt-2">
              {["backgroundColor", "titleColor", "descriptionColor"].map(
                (name) => (
                  <div key={name} className="flex flex-col items-center">
                    <label className="mb-2 text-sm font-medium text-gray-700">
                      {name === "backgroundColor"
                        ? "Background"
                        : name === "titleColor"
                        ? "Title"
                        : "Description"}
                    </label>
                    <div
                      className="w-12 h-12 rounded-full cursor-pointer border-2"
                      style={{ backgroundColor: form[name] }}
                    >
                      <input
                        type="color"
                        name={name}
                        value={form[name]}
                        onChange={handleChange}
                        className="w-12 h-12 opacity-0 cursor-pointer rounded-full"
                      />
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="flex flex-wrap gap-2 max-w-full overflow-x-auto mt-2">
              {galleryData.map((img) => (
                <img
                  key={img.id}
                  src={img.url}
                  alt="gallery"
                  className={`w-20 h-20 object-cover rounded cursor-pointer ${
                    selectedImage === img.url ? "ring-2 ring-blue-500" : ""
                  }`}
                  onClick={() => selectFromGallery(img.url)}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={Loading || uploadingImage}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white p-2 rounded mt-4"
            >
              {Loading ? "Creating..." : "Create Banner"}
            </button>

            <button
              type="button"
              className="w-full bg-gray-300 hover:bg-gray-400 text-black p-2 rounded mt-2"
              onClick={() => {
                resetForm();
                setIsCreatingBanner(false);
              }}
            >
              Cancel
            </button>
          </form>

          <div
            className="flex-1 flex flex-col items-center justify-center p-6 rounded-lg shadow"
            style={{ backgroundColor: form.backgroundColor }}
          >
            {selectedImage && (
              <img
                src={selectedImage}
                alt={form.imageTitle}
                className="w-full h-60 object-cover rounded mb-4"
              />
            )}
            <h3
              style={{ color: form.titleColor }}
              className="text-xl font-bold"
            >
              {form.imageTitle || "Preview Title"}
            </h3>
            <p style={{ color: form.descriptionColor }} className="mt-2">
              {form.imageDesctription ||
                "Preview description will appear here."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewBanner;
